import { useCurrentStateAndParams } from "@uirouter/react";
import { isEmpty } from "lodash";
import queryString from "query-string";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { TabList, TabPanel, Tabs } from "@similarweb/ui-components";

import type { getKeywordsDrilldownTableColumns } from "app/pages/keyword-research/website-analysis/pages/landing-pages/OrganicLandingPagesColumnsConfigV2";
import { tabsMapping } from "app/pages/keyword-research/website-analysis/pages/landing-pages/OrganicLandingPagesColumnsConfigV2";
import {
    ButtonStyled,
    CloseIconButton,
    DataContainer,
    HeaderTitle,
    IndexContainer,
    PageContainer,
    TabStyled,
    TopContainer,
    TrafficShareContainer,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/StyledComponents";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import DownloadButtonMenu from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import { TrafficShareWithVisits, UrlCellWebsiteAnalysis } from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";
import { getDomainWithSubs } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { OrganicLandingPageEnrichedAdsTable } from "./OrganicLandingPageEnrichedAdsTable";
import { OrganicLandingPageEnrichedKeywordsTable } from "./OrganicLandingPageEnrichedKeywordsTable";
import { OrganicLandingPageEnrichedTrend } from "./OrganicLandingPageEnrichedTrend";
import { useLandingPagesEndpoints } from "./landingPagesEndpoints";
import { sourceTypeMap } from "app/common/keyword-research/landing-pages/landingPagesTypes";

const TabIcon = ({ iconName }: { iconName: IconName }) => (
    <SWReactIcons
        iconName={iconName}
        size="xs"
        hoverIconColor={colorsPalettes.carbon[500]}
    />
);

enum EOrganicLandingPagesV2Tabs {
    Trend = 0,
    Keywords = 1,
    Ads = 2,
}

interface IOrganicLandingPagesEnrichedV2Props {
    pageSize: number;
    pageNumber: number;
    row: any;
    selectedDomain: string;
    sort: string;
    asc: boolean;
    selectedRows: any;
    selectedTabIndex: number;
    setSelectedTabIndex: (index: number) => void;
    clearAllSelectedRows: VoidFunction;
    showToast: (href: string, text: string, label: string) => void;
    excelAllowed: boolean;
    keywordTargetState: string;
    keywordSerpTargetState: string;
    searchAdsTargetState: string;
    getKeywordExpansionColumnsFunction?: typeof getKeywordsDrilldownTableColumns;
    onSeeAllClick?: (
        trackingEventSuffix: string,
        state: string,
        additionalParams?: Record<string, unknown>,
    ) => (url: string) => void;
}

const OrganicLandingPagesEnrichedV2 = (props: IOrganicLandingPagesEnrichedV2Props) => {
    const { params } = useCurrentStateAndParams();
    const endpoints = useLandingPagesEndpoints();

    const defaultOnSeeAllClick =
        (trackingEventSuffix: string, state: string, additionalParams = {}) =>
        (url: string) => {
            allTrackers.dispatchTrackEvent("Button", "click", `See all ${trackingEventSuffix}`);

            const pageFilter: IChosenItemParams = [{ url, searchType: "exact" }];
            const key = getDomainWithSubs(url);
            void proNavigator.go(state, {
                ...params,
                ...additionalParams,
                key,
                pageFilter: transformSiteListToParam(pageFilter),
            });
        };

    const {
        pageSize,
        pageNumber,
        row,
        selectedDomain,
        sort,
        asc,
        excelAllowed,
        selectedTabIndex,
        keywordTargetState,
        keywordSerpTargetState,
        searchAdsTargetState,
        getKeywordExpansionColumnsFunction,
        onSeeAllClick = defaultOnSeeAllClick,
    } = props;
    const { Url, index, ClicksShare } = row;
    const { from, to, isWindow } = DurationService.getDurationData(params.duration).forAPI;
    const pagesParam = JSON.parse(params.pageFilter);
    const page = pagesParam.find((pageObj) => pageObj.url === Url);
    const sourceType = sourceTypeMap[params.selectedPageTab];
    const queryParams = {
        country: params.country,
        webSource: params.webSource,
        includeSubDomains: true,
        isWindow: isWindow,
        from,
        to,
        pageFilterJson: JSON.stringify([page]),
        landingPage: Url,
        key: selectedDomain,
        sort,
        asc,
        sourceType,
    };
    const isPaid = sourceType === sourceTypeMap.Paid;
    const excelUrl = `${endpoints.drillDownExcel}?${queryString.stringify(queryParams)}`;

    const clickOutsideXButton = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Organic Pages V2/Trend Drilldown");
        document.body.click();
    };

    const onTabSelect = (tabIndex: number) => {
        allTrackers.dispatchTrackEvent(
            "Tab",
            "click",
            `clicked drilldown tab/${Object.keys(tabsMapping).find((key) => tabsMapping[key] === tabIndex) || ""}`,
        );
        props.setSelectedTabIndex(tabIndex);
    };

    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "click", "Organic Pages Keywords Table Drilldown/Excel ");
    };

    const displaySeeAllCta =
        selectedTabIndex === EOrganicLandingPagesV2Tabs.Keywords || selectedTabIndex === EOrganicLandingPagesV2Tabs.Ads;
    const seeAllCtaText =
        selectedTabIndex === EOrganicLandingPagesV2Tabs.Keywords
            ? "organic.landing.pages.v2.enriched.get.all.keywords"
            : "organic.landing.pages.v2.enriched.get.all.ads";
    const seeAllCtaCallback =
        selectedTabIndex === EOrganicLandingPagesV2Tabs.Keywords
            ? () => onSeeAllClick("keywords", keywordTargetState)(Url)
            : () => onSeeAllClick("ads", searchAdsTargetState, { selectedPageTab: EAdsTabs[EAdsTabs.Text] })(Url);

    return (
        <>
            <TopContainer>
                <DataContainer>
                    <IndexContainer>{pageNumber * pageSize + index + 1}</IndexContainer>
                    <PageContainer className={"swReactTableCell"}>
                        <UrlCellWebsiteAnalysis
                            {...props.row}
                            value={Url}
                            row={props.row}
                        />
                    </PageContainer>
                    <TrafficShareContainer>
                        <HeaderTitle>{translate("organic.landing.pages.v2.enriched.share.title")}</HeaderTitle>
                        <TrafficShareWithVisits
                            {...props}
                            applyAbbrNumberFilter
                            totalVisitsRowProp="Clicks"
                            thresholdForTotalFilter={50}
                            value={ClicksShare}
                            thresholdTrafficShareWithVisitsPercentage={0.01}
                            displayLessThanThreshold={true}
                        />
                    </TrafficShareContainer>
                </DataContainer>
                <>
                    {displaySeeAllCta ? (
                        <ButtonStyled
                            type="primary"
                            onClick={seeAllCtaCallback}
                        >
                            {translate(seeAllCtaText)}
                        </ButtonStyled>
                    ) : null}
                    {selectedTabIndex === EOrganicLandingPagesV2Tabs.Keywords ? (
                        <DownloadButtonMenu
                            Excel={true}
                            downloadUrl={excelUrl}
                            exportFunction={trackExcelDownload}
                            excelLocked={!excelAllowed}
                            downloadTooltipText={translate("ranking.distribution.csv.downloadCSV")}
                        />
                    ) : null}
                </>
                <CloseIconButton
                    type="flat"
                    onClick={clickOutsideXButton}
                    iconName="clear"
                    placement="left"
                />
            </TopContainer>
            <Tabs
                selectedIndex={selectedTabIndex}
                onSelect={onTabSelect}
            >
                <TabList>
                    <TabStyled disabled={isEmpty(row.Trend)}>
                        <TabIcon iconName="trend-up" />
                        {translate("organic.landing.pages.2.0.enriched.tables.tabs.clicks.trend")}
                    </TabStyled>
                    <TabStyled>
                        <TabIcon iconName="search-keywords" />
                        {translate("organic.landing.pages.2.0.enriched.tables.tabs.keywords")}
                    </TabStyled>
                    {isPaid ? (
                        <TabStyled>
                            <TabIcon iconName="dollar-sign" />
                            {translate("organic.landing.pages.2.0.enriched.tables.tabs.ads")}
                        </TabStyled>
                    ) : null}
                </TabList>
                <TabPanel>
                    <OrganicLandingPageEnrichedTrend
                        selectedDomain={selectedDomain}
                        landingPage={Url}
                    />
                </TabPanel>
                <TabPanel>
                    <OrganicLandingPageEnrichedKeywordsTable
                        row={row}
                        selectedDomain={selectedDomain}
                        asc={asc}
                        sort={sort}
                        keywordTargetState={keywordSerpTargetState}
                        getKeywordExpansionColumnsFunction={getKeywordExpansionColumnsFunction}
                    />
                </TabPanel>
                <TabPanel>
                    <OrganicLandingPageEnrichedAdsTable
                        landingPage={Url}
                        asc={asc}
                        sort={sort}
                    />
                </TabPanel>
            </Tabs>
        </>
    );
};

export default OrganicLandingPagesEnrichedV2;
